import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { isUserEditor, isUserAdmin, refreshUserToken } from '../firebase/auth'
import { getLinkedInSettings } from '../firebase/settings'
import { getProductSchedule, saveProductSchedule } from '../firebase/productSchedule'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../firebase/config'
import './LinkedInPosts.css'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** ISO week number for the 1st of the given month in the given year (1–53). */
function getISOWeekNumber(year, monthIndex) {
  const date = new Date(year, monthIndex, 1)
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7))
  const startOfYear = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  return Math.ceil((((d - startOfYear) / 86400000) + 1) / 7)
}

const TIMEZONE_OPTIONS = (() => {
  try {
    if (typeof Intl !== 'undefined' && Intl.supportedValuesOf) {
      return Intl.supportedValuesOf('timeZone').sort()
    }
  } catch (e) { /* ignore */ }
  return ['UTC', 'Europe/London', 'Europe/Paris', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Asia/Tokyo', 'Asia/Singapore', 'Australia/Sydney']
})()

const PRODUCT_SCHEDULE_DEFAULTS = [
  {
    id: 'firewall',
    name: 'Firewall',
    months: [
      { theme: 'Operational Reset', contentReady: false, bestSlot: 'Tue 9am' },
      { theme: 'Process Maturity', contentReady: false, bestSlot: 'Thu 10am' },
      { theme: 'Compliance Prep', contentReady: false, bestSlot: 'Tue 9am' },
      { theme: 'Risk Reduction', contentReady: false, bestSlot: 'Thu 10am' },
      { theme: 'Efficiency', contentReady: false, bestSlot: 'Tue 9am' },
      { theme: 'Case Scenarios', contentReady: false, bestSlot: 'Wed 10am' },
      { theme: 'Simplification', contentReady: false, bestSlot: 'Tue 9am' },
      { theme: 'Visibility', contentReady: false, bestSlot: 'Wed 9am' },
      { theme: 'Audit Season', contentReady: false, bestSlot: 'Tue 9am' },
      { theme: 'Security Ops', contentReady: false, bestSlot: 'Thu 10am' },
      { theme: 'Lessons Learned', contentReady: false, bestSlot: 'Tue 9am' },
      { theme: 'Year-in-Review', contentReady: false, bestSlot: 'Thu 9am' }
    ]
  },
  {
    id: 'secure-code-cards',
    name: 'Secure Code Cards',
    months: [
      { theme: 'Learning Goals', contentReady: false, bestSlot: 'Wed 12pm' },
      { theme: 'OWASP Basics', contentReady: false, bestSlot: 'Fri 9am' },
      { theme: 'Hands-On', contentReady: false, bestSlot: 'Wed 12pm' },
      { theme: 'Dev Culture', contentReady: false, bestSlot: 'Fri 10am' },
      { theme: 'AppSec Awareness', contentReady: false, bestSlot: 'Wed 12pm' },
      { theme: 'Team Learning', contentReady: false, bestSlot: 'Wed 12pm' },
      { theme: 'Light Learning', contentReady: false, bestSlot: 'Fri 9am' },
      { theme: 'Gamified Security', contentReady: false, bestSlot: 'Wed 12pm' },
      { theme: 'Back-to-Skills', contentReady: false, bestSlot: 'Wed 12pm' },
      { theme: 'Secure Coding Month', contentReady: false, bestSlot: 'Tue 12pm' },
      { theme: 'Knowledge Gaps', contentReady: false, bestSlot: 'Fri 9am' },
      { theme: 'Year Wrap', contentReady: false, bestSlot: 'Wed 12pm' }
    ]
  },
  {
    id: 'spectra',
    name: 'Spectra',
    months: [
      { theme: 'Risk Strategy', contentReady: false, bestSlot: 'Tue 9am' },
      { theme: 'Governance', contentReady: false, bestSlot: 'Thu 10am' },
      { theme: 'Exceptions', contentReady: false, bestSlot: 'Tue 9am' },
      { theme: 'Control Mapping', contentReady: false, bestSlot: 'Thu 10am' },
      { theme: 'Decision Support', contentReady: false, bestSlot: 'Tue 9am' },
      { theme: 'Audit Readiness', contentReady: false, bestSlot: 'Thu 10am' },
      { theme: 'Risk Drift', contentReady: false, bestSlot: 'Tue 9am' },
      { theme: 'Simplification', contentReady: false, bestSlot: 'Wed 9am' },
      { theme: 'Board Reporting', contentReady: false, bestSlot: 'Thu 10am' },
      { theme: 'Risk Awareness', contentReady: false, bestSlot: 'Tue 9am' },
      { theme: 'Lessons Learned', contentReady: false, bestSlot: 'Thu 10am' },
      { theme: 'Annual Review', contentReady: false, bestSlot: 'Tue 9am' }
    ]
  },
  {
    id: 'saas-security-score',
    name: 'SaaS Security Score',
    months: [
      { theme: 'SaaS Inventory', contentReady: false, bestSlot: 'Tue 9am' },
      { theme: 'Identity Risk', contentReady: false, bestSlot: 'Thu 10am' },
      { theme: 'Configuration Risk', contentReady: false, bestSlot: 'Tue 9am' },
      { theme: 'Vendor Risk', contentReady: false, bestSlot: 'Thu 10am' },
      { theme: 'Cost & Risk', contentReady: false, bestSlot: 'Wed 10am' },
      { theme: 'Metrics', contentReady: false, bestSlot: 'Tue 9am' },
      { theme: 'Shadow SaaS', contentReady: false, bestSlot: 'Wed 9am' },
      { theme: 'Visibility', contentReady: false, bestSlot: 'Tue 9am' },
      { theme: 'Compliance', contentReady: false, bestSlot: 'Thu 10am' },
      { theme: 'Attack Surface', contentReady: false, bestSlot: 'Tue 9am' },
      { theme: 'Optimisation', contentReady: false, bestSlot: 'Wed 10am' },
      { theme: 'Year-in-Review', contentReady: false, bestSlot: 'Tue 9am' }
    ]
  },
  {
    id: 'aisec-training',
    name: 'AISec Training',
    months: [
      { theme: 'Upskilling', contentReady: false, bestSlot: 'Wed 12pm' },
      { theme: 'AI Governance', contentReady: false, bestSlot: 'Thu 12pm' },
      { theme: 'AI Risk', contentReady: false, bestSlot: 'Wed 12pm' },
      { theme: 'Practical Skills', contentReady: false, bestSlot: 'Thu 12pm' },
      { theme: 'Leadership', contentReady: false, bestSlot: 'Wed 12pm' },
      { theme: 'Career Growth', contentReady: false, bestSlot: 'Thu 12pm' },
      { theme: 'Awareness', contentReady: false, bestSlot: 'Wed 12pm' },
      { theme: 'Skill Refresh', contentReady: false, bestSlot: 'Wed 12pm' },
      { theme: 'AI Maturity', contentReady: false, bestSlot: 'Thu 12pm' },
      { theme: 'AI Security Month', contentReady: false, bestSlot: 'Tue 12pm' },
      { theme: 'Case Studies', contentReady: false, bestSlot: 'Thu 12pm' },
      { theme: 'Reflection', contentReady: false, bestSlot: 'Wed 12pm' }
    ]
  }
]

const LinkedInPosts = ({ user }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isEditor, setIsEditor] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [testingShareAPI, setTestingShareAPI] = useState(false)
  const [shareTestResult, setShareTestResult] = useState(null)
  const [generatingOAuthUrl, setGeneratingOAuthUrl] = useState(false)
  const [oauthUrlResult, setOauthUrlResult] = useState(null)
  const [authCode, setAuthCode] = useState('')
  const [exchangingCode, setExchangingCode] = useState(false)
  const [exchangeResult, setExchangeResult] = useState(null)
  const [showManualPaste, setShowManualPaste] = useState(false)
  const [postText, setPostText] = useState('')
  const [postVisibility, setPostVisibility] = useState('PUBLIC')
  const [postImageFile, setPostImageFile] = useState(null)
  const [postImagePreview, setPostImagePreview] = useState(null)
  const [postImageAlt, setPostImageAlt] = useState('')
  // Posting is company-page only; personal profile posting is disabled
  const postToCompany = true
  const [linkedInSettingsLoaded, setLinkedInSettingsLoaded] = useState(false)
  const [organizationId, setOrganizationId] = useState('')
  const [companyUrl, setCompanyUrl] = useState('')
  const [submittingPost, setSubmittingPost] = useState(false)
  const [postResult, setPostResult] = useState(null)
  const [activeTab, setActiveTab] = useState('posts')
  const [scheduleYear, setScheduleYear] = useState(() => new Date().getFullYear())
  const [scheduleEdits, setScheduleEdits] = useState({})
  const [scheduleLoadingYear, setScheduleLoadingYear] = useState(null)
  const [scheduleSaving, setScheduleSaving] = useState(false)

  const getScheduleCell = (year, productId, monthIndex, field) => {
    const edit = scheduleEdits[year]?.[productId]?.[monthIndex]?.[field]
    if (field === 'week') {
      if (edit !== undefined && edit !== '') return edit
      return String(getISOWeekNumber(year, monthIndex))
    }
    if (field === 'contentReady') {
      if (edit === true || edit === false) return edit
      const product = PRODUCT_SCHEDULE_DEFAULTS.find(p => p.id === productId)
      return product?.months[monthIndex]?.contentReady ?? false
    }
    if (edit !== undefined) return edit
    const product = PRODUCT_SCHEDULE_DEFAULTS.find(p => p.id === productId)
    return product?.months[monthIndex]?.[field] ?? ''
  }

  const updateScheduleCell = (year, productId, monthIndex, field, value) => {
    setScheduleEdits(prev => ({
      ...prev,
      [year]: {
        ...prev[year],
        [productId]: {
          ...prev[year]?.[productId],
          [monthIndex]: {
            ...prev[year]?.[productId]?.[monthIndex],
            [field]: value
          }
        }
      }
    }))
  }

  const getCustomRows = (year) => {
    const rows = scheduleEdits[year]?.customRows
    return Array.isArray(rows) ? rows : []
  }

  const addCustomRow = (year) => {
    const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    setScheduleEdits(prev => ({
      ...prev,
      [year]: {
        ...prev[year],
        customRows: [...(Array.isArray(prev[year]?.customRows) ? prev[year].customRows : []), { id, month: '', week: '', product: '', theme: '', contentReady: false, bestSlot: '', timezone: '' }]
      }
    }))
  }

  const updateCustomRow = (year, rowId, field, value) => {
    setScheduleEdits(prev => {
      const rows = Array.isArray(prev[year]?.customRows) ? prev[year].customRows : []
      const next = rows.map(row =>
        row.id === rowId ? { ...row, [field]: value } : row
      )
      return { ...prev, [year]: { ...prev[year], customRows: next } }
    })
  }

  const removeCustomRow = (year, rowId) => {
    setScheduleEdits(prev => ({
      ...prev,
      [year]: {
        ...prev[year],
        customRows: (prev[year]?.customRows || []).filter(row => row.id !== rowId)
      }
    }))
  }

  const getDeletedDefaultRows = (year) => {
    const list = scheduleEdits[year]?.deletedDefaultRows
    return Array.isArray(list) ? list : []
  }

  const isDefaultRowDeleted = (year, monthIndex, productId) => {
    return getDeletedDefaultRows(year).includes(`${monthIndex}-${productId}`)
  }

  const deleteDefaultRow = (year, monthIndex, productId) => {
    const key = `${monthIndex}-${productId}`
    setScheduleEdits(prev => ({
      ...prev,
      [year]: {
        ...prev[year],
        deletedDefaultRows: [...(Array.isArray(prev[year]?.deletedDefaultRows) ? prev[year].deletedDefaultRows : []), key]
      }
    }))
  }

  const restoreDefaultRow = (year, monthIndex, productId) => {
    const key = `${monthIndex}-${productId}`
    setScheduleEdits(prev => ({
      ...prev,
      [year]: {
        ...prev[year],
        deletedDefaultRows: (prev[year]?.deletedDefaultRows || []).filter(k => k !== key)
      }
    }))
  }

  useEffect(() => {
    const checkUserStatus = async () => {
      if (user) {
        // Force refresh token to get latest custom claims
        await refreshUserToken(user)
        
        const [editorStatus, adminStatus] = await Promise.all([
          isUserEditor(user, true),
          isUserAdmin(user, true)
        ])
        setIsEditor(editorStatus)
        setIsAdmin(adminStatus)
      }
      setLoading(false)
    }
    checkUserStatus()
  }, [user])

  // Load Organization ID and Company Page URL from Admin settings (set in Admin → LinkedIn Settings)
  useEffect(() => {
    if (!user || (!isEditor && !isAdmin) || loading) return
    const load = async () => {
      try {
        const settings = await getLinkedInSettings()
        if (settings) {
          setOrganizationId(settings.organizationId || '')
          setCompanyUrl(settings.companyPageUrl || '')
        }
      } catch (err) {
        console.error('Error loading LinkedIn settings:', err)
      } finally {
        setLinkedInSettingsLoaded(true)
      }
    }
    load()
  }, [user, isEditor, isAdmin, loading])

  // Load product schedule from Firestore when Schedule tab is active and year changes
  useEffect(() => {
    if (activeTab !== 'schedule' || !user) return
    const year = scheduleYear
    setScheduleLoadingYear(year)
    let cancelled = false
    getProductSchedule(year)
      .then((data) => {
        if (!cancelled) {
          setScheduleEdits(prev => ({ ...prev, [year]: data || {} }))
        }
      })
      .catch((err) => {
        if (!cancelled) console.error('Error loading product schedule:', err)
      })
      .finally(() => {
        if (!cancelled) setScheduleLoadingYear(null)
      })
    return () => { cancelled = true }
  }, [activeTab, scheduleYear, user])

  // Debounced save of product schedule to Firestore (admin/editor only; rules enforce)
  useEffect(() => {
    if (activeTab !== 'schedule') return
    const year = scheduleYear
    const edits = scheduleEdits[year]
    if (!edits || Object.keys(edits).length === 0) return
    const timeoutId = setTimeout(() => {
      setScheduleSaving(true)
      saveProductSchedule(year, edits)
        .catch((err) => console.error('Error saving product schedule:', err))
        .finally(() => setScheduleSaving(false))
    }, 1200)
    return () => clearTimeout(timeoutId)
  }, [activeTab, scheduleYear, scheduleEdits])

  // After OAuth callback redirect with ?linkedin=success, run Share API test and clear param
  useEffect(() => {
    if (loading) return
    if (searchParams.get('linkedin') !== 'success') return
    setSearchParams({}, { replace: true })
    handleTestShareAPI()
  }, [loading, searchParams])

  const handleTestShareAPI = async () => {
    setTestingShareAPI(true)
    setShareTestResult(null)
    
    try {
      const testLinkedInShareAPI = httpsCallable(functions, 'testLinkedInShareAPI')
      const result = await testLinkedInShareAPI()
      setShareTestResult(result.data)
    } catch (error) {
      console.error('Error testing LinkedIn Share API:', error)
      setShareTestResult({
        success: false,
        message: error.message || 'Failed to test LinkedIn Share API. Please try again.',
        error: error.code || 'Unknown Error'
      })
    } finally {
      setTestingShareAPI(false)
    }
  }

  const handleGenerateOAuthUrl = async () => {
    setGeneratingOAuthUrl(true)
    setOauthUrlResult(null)
    setExchangeResult(null)
    setAuthCode('')
    
    try {
      const generateLinkedInOAuthUrl = httpsCallable(functions, 'generateLinkedInOAuthUrl')
      const result = await generateLinkedInOAuthUrl()
      setOauthUrlResult(result.data)
    } catch (error) {
      console.error('Error generating LinkedIn OAuth URL:', error)
      setOauthUrlResult({
        success: false,
        message: error.message || 'Failed to generate OAuth URL. Please try again.',
        error: error.code || 'Unknown Error'
      })
    } finally {
      setGeneratingOAuthUrl(false)
    }
  }

  /** Connect with w_member_social only — works even before "Share on LinkedIn" is approved. */
  const handleConnectLinkedIn = async () => {
    setGeneratingOAuthUrl(true)
    setOauthUrlResult(null)
    setExchangeResult(null)
    setAuthCode('')
    try {
      const generateLinkedInOAuthUrl = httpsCallable(functions, 'generateLinkedInOAuthUrl')
      const result = await generateLinkedInOAuthUrl()
      const data = result.data
      setOauthUrlResult(data)
      if (data?.success && data?.authUrl) {
        window.open(data.authUrl, '_blank', 'noopener,noreferrer')
      }
    } catch (error) {
      console.error('Error generating LinkedIn OAuth URL:', error)
      setOauthUrlResult({
        success: false,
        message: error.message || 'Failed to generate OAuth URL. Please try again.',
        error: error.code || 'Unknown Error'
      })
    } finally {
      setGeneratingOAuthUrl(false)
    }
  }

  /** Re-authorize including w_organization_social for company page posting. Use after "Share on LinkedIn" is approved. */
  const handleReauthorizeForCompany = async () => {
    setGeneratingOAuthUrl(true)
    setOauthUrlResult(null)
    setExchangeResult(null)
    setAuthCode('')
    try {
      const generateLinkedInOAuthUrl = httpsCallable(functions, 'generateLinkedInOAuthUrl')
      const result = await generateLinkedInOAuthUrl({ includeOrganizationScope: true })
      const data = result.data
      setOauthUrlResult(data)
      if (data?.success && data?.authUrl) {
        window.open(data.authUrl, '_blank', 'noopener,noreferrer')
      }
    } catch (error) {
      console.error('Error generating LinkedIn OAuth URL:', error)
      setOauthUrlResult({
        success: false,
        message: error.message || 'Failed to generate OAuth URL. Please try again.',
        error: error.code || 'Unknown Error'
      })
    } finally {
      setGeneratingOAuthUrl(false)
    }
  }

  const handleExchangeAuthCode = async () => {
    if (!authCode.trim()) {
      setExchangeResult({
        success: false,
        message: 'Please enter an authorization code.'
      })
      return
    }

    setExchangingCode(true)
    setExchangeResult(null)
    
    // Extract code from URL if user pasted the full redirect URL
    let codeToUse = authCode.trim()
    try {
      const url = new URL(codeToUse)
      const codeParam = url.searchParams.get('code')
      if (codeParam) {
        codeToUse = codeParam
        setAuthCode(codeParam) // Update the input with just the code
      }
    } catch (e) {
      // Not a URL, use as-is
    }
    
    try {
      const exchangeLinkedInAuthCode = httpsCallable(functions, 'exchangeLinkedInAuthCode')
      const result = await exchangeLinkedInAuthCode({
        code: codeToUse,
        redirectUri: oauthUrlResult?.redirectUri || (typeof window !== 'undefined' ? `${window.location.origin}/linkedin-posts/callback` : 'https://www.linkedin.com/developers/tools/oauth/redirect')
      })
      setExchangeResult(result.data)
      
      if (result.data.success) {
        // Clear the auth code input
        setAuthCode('')
        // Optionally refresh the test to verify the new token works
        setTimeout(() => {
          handleTestShareAPI()
        }, 1000)
      }
    } catch (error) {
      console.error('Error exchanging authorization code:', error)
      setExchangeResult({
        success: false,
        message: error.message || 'Failed to exchange authorization code. Please try again.',
        error: error.code || 'Unknown Error'
      })
    } finally {
      setExchangingCode(false)
    }
  }

  const MAX_IMAGE_MB = 5
  const MAX_IMAGE_BYTES = MAX_IMAGE_MB * 1024 * 1024

  const handlePostImageChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) {
      if (postImagePreview) URL.revokeObjectURL(postImagePreview)
      setPostImageFile(null)
      setPostImagePreview(null)
      return
    }
    if (!file.type.startsWith('image/')) {
      setPostResult({ success: false, message: 'Please choose an image file (JPEG, PNG, or GIF).' })
      return
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setPostResult({ success: false, message: `Image must be under ${MAX_IMAGE_MB}MB.` })
      return
    }
    if (postImagePreview) URL.revokeObjectURL(postImagePreview)
    setPostImageFile(file)
    setPostImagePreview(URL.createObjectURL(file))
    setPostResult(null)
  }

  const clearPostImage = () => {
    if (postImagePreview) URL.revokeObjectURL(postImagePreview)
    setPostImageFile(null)
    setPostImagePreview(null)
    setPostImageAlt('')
    setPostResult(null)
  }

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(file)
    })

  const handleSubmitPost = async (e) => {
    e.preventDefault()
    
    if (!postText.trim()) {
      setPostResult({
        success: false,
        message: 'Please enter some text for your post.'
      })
      return
    }

    setSubmittingPost(true)
    setPostResult(null)
    
    try {
      const createLinkedInPost = httpsCallable(functions, 'createLinkedInPost')
      const payload = {
        text: postText.trim(),
        visibility: postVisibility,
        postToCompany: postToCompany,
        organizationId: postToCompany ? organizationId : null,
        companyUrl: postToCompany ? companyUrl : null
      }
      if (postImageFile) {
        payload.imageBase64 = await fileToBase64(postImageFile)
        if (postImageAlt.trim()) payload.imageAltText = postImageAlt.trim()
      }
      const result = await createLinkedInPost(payload)
      setPostResult(result.data)
      
      if (result.data.success) {
        setPostText('')
        setPostVisibility('PUBLIC')
        clearPostImage()
      }
    } catch (error) {
      console.error('Error creating LinkedIn post:', error)
      setPostResult({
        success: false,
        message: error.message || 'Failed to create LinkedIn post. Please try again.',
        error: error.code || 'Unknown Error'
      })
    } finally {
      setSubmittingPost(false)
    }
  }

  if (loading) {
    return (
      <div className="linkedin-posts-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  if (!isEditor && !isAdmin) {
    return (
      <div className="linkedin-posts-container">
        <div className="linkedin-posts-access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page. Only administrators and editors can access LinkedIn Posts.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="linkedin-posts-container">
      <div className="linkedin-posts-header">
        <h1>LinkedIn Posts</h1>
        <p className="linkedin-posts-subtitle">Manage and create LinkedIn posts</p>
      </div>
      
      <div className="linkedin-posts-content">
        <div className="linkedin-posts-tabs">
          <button
            type="button"
            className={`linkedin-posts-tab ${activeTab === 'posts' ? 'active' : ''}`}
            onClick={() => setActiveTab('posts')}
          >
            Posts
          </button>
          <button
            type="button"
            className={`linkedin-posts-tab ${activeTab === 'connection' ? 'active' : ''}`}
            onClick={() => setActiveTab('connection')}
          >
            Connect LinkedIn
          </button>
          <button
            type="button"
            className={`linkedin-posts-tab ${activeTab === 'schedule' ? 'active' : ''}`}
            onClick={() => setActiveTab('schedule')}
          >
            Schedule
          </button>
        </div>

        {activeTab === 'connection' && (
          <div className="linkedin-posts-section">
            <div className="linkedin-posts-section-header">
              <h2>Connect LinkedIn</h2>
              <p className="section-description">Grant permissions to post. Connect requests both personal and company page scopes (<code>w_member_social</code> + <code>w_organization_social</code>). Use the new app Client ID in Admin Settings.</p>
            </div>
            
            <div className="api-test-container">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleConnectLinkedIn}
                  disabled={generatingOAuthUrl}
                  style={{ fontSize: '1rem', padding: '0.75rem 1.5rem' }}
                >
                  {generatingOAuthUrl ? 'Opening…' : 'Connect LinkedIn (grant permissions)'}
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={handleReauthorizeForCompany}
                  disabled={generatingOAuthUrl}
                  style={{ fontSize: '0.9rem', padding: '0.75rem 1.25rem', background: 'rgba(59, 130, 246, 0.15)', color: 'var(--primary, #2563eb)' }}
                >
                  {generatingOAuthUrl ? 'Opening…' : 'Re-authorize for company page'}
                </button>
              </div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginTop: '0.5rem' }}>
                Connect requests both scopes. Use <strong>Re-authorize for company page</strong> if you previously connected with personal scope only.
              </p>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-light)', marginTop: '0.75rem' }}>
                A new tab will open. Sign in with LinkedIn and grant access. You'll be redirected back here when done.
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '0.5rem' }}>
                Add this URL in Admin Settings and in your LinkedIn app: <code style={{ background: 'rgba(0,0,0,0.08)', padding: '2px 6px', borderRadius: '4px' }}>{typeof window !== 'undefined' ? `${window.location.origin}/linkedin-posts/callback` : '/linkedin-posts/callback'}</code>
              </p>
              <button
                type="button"
                className="btn"
                onClick={handleTestShareAPI}
                disabled={testingShareAPI}
                style={{ marginTop: '0.5rem', fontSize: '0.85rem', background: 'transparent', color: 'var(--text-light)' }}
              >
                {testingShareAPI ? 'Checking…' : 'Verify connection'}
              </button>
              {oauthUrlResult && !oauthUrlResult.success && (
                <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '4px' }}>
                  <strong>Error:</strong> {oauthUrlResult.message || 'Failed to generate OAuth URL'}
                </div>
              )}

              <div style={{ marginTop: '1rem' }}>
                {!showManualPaste ? (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setShowManualPaste(true)}
                    style={{ fontSize: '0.85rem', background: 'transparent', color: 'var(--text-light)' }}
                  >
                    Paste code manually instead
                  </button>
                ) : (
                  <div style={{ padding: '0.75rem', background: 'rgba(0,0,0,0.04)', borderRadius: '6px' }}>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--text)' }}>
                      Authorization code (or full redirect URL)
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                      <input
                        type="text"
                        value={authCode}
                        onChange={(e) => setAuthCode(e.target.value)}
                        placeholder="Paste code or URL…"
                        style={{ flex: 1, padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }}
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleExchangeAuthCode}
                        disabled={exchangingCode || !authCode.trim()}
                        style={{ fontSize: '0.85rem', padding: '0.5rem 1rem', whiteSpace: 'nowrap' }}
                      >
                        {exchangingCode ? 'Finishing…' : 'Finish'}
                      </button>
                    </div>
                    {exchangeResult && (
                      <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: exchangeResult.success ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)', borderRadius: '4px', fontSize: '0.85rem' }}>
                        {exchangeResult.success ? '✅ ' : '❌ '}{exchangeResult.message}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {shareTestResult && (
                <div className={`test-result ${shareTestResult.success ? 'success' : 'error'}`} style={{ marginTop: '1rem' }}>
                  <div className="test-result-header">
                    <span className="test-result-icon">
                      {shareTestResult.success ? '✅' : '❌'}
                    </span>
                    <span className="test-result-title">
                      {shareTestResult.success ? 'Share API Connection Successful' : 'Share API Connection Failed'}
                    </span>
                  </div>
                  <div className="test-result-message">
                    {shareTestResult.message}
                  </div>
                  {shareTestResult.success && shareTestResult.hasOrganizationSocial === false && (
                    <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '6px', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                      <p style={{ fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: '600' }}>
                        Token has personal scope only. To publish to your company page, get a new token with <code>w_organization_social</code>.
                      </p>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleReauthorizeForCompany}
                        disabled={generatingOAuthUrl}
                        style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                      >
                        {generatingOAuthUrl ? 'Opening…' : 'Re-authorize for company page'}
                      </button>
                    </div>
                  )}
                  {shareTestResult.userInfo && (
                    <div className="test-result-info" style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '4px' }}>
                      <strong>User Info:</strong>
                      <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                        <li>ID: {shareTestResult.userInfo.id}</li>
                        {shareTestResult.userInfo.name !== 'N/A' && <li>Name: {shareTestResult.userInfo.name}</li>}
                        {shareTestResult.userInfo.email !== 'N/A' && <li>Email: {shareTestResult.userInfo.email}</li>}
                      </ul>
                      {shareTestResult.hasRedirectUrl && (
                        <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.85rem', fontStyle: 'italic', color: 'var(--text-light)' }}>
                          ✓ Redirect URL configured - ready for OAuth flows
                        </p>
                      )}
                    </div>
                  )}
                  {shareTestResult.error && (
                    <div className="test-result-error">
                      <strong>Error:</strong> {shareTestResult.error}
                    </div>
                  )}
                  {shareTestResult.statusCode && (
                    <div className="test-result-info">
                      <strong>HTTP Status:</strong> {shareTestResult.statusCode}
                    </div>
                  )}
                  {shareTestResult.tokenExpired && (
                    <div className="test-result-info" style={{ marginTop: '0.5rem', padding: '0.75rem', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '4px' }}>
                      <strong>⚠️ Token Expired:</strong> Refresh by connecting again.
                      <div style={{ marginTop: '0.75rem' }}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleConnectLinkedIn}
                          disabled={generatingOAuthUrl}
                          style={{ fontSize: '0.9rem', padding: '0.5rem 1rem', marginTop: '0.5rem' }}
                        >
                          {generatingOAuthUrl ? 'Opening…' : 'Connect LinkedIn (grant permissions)'}
                        </button>
                      </div>
                    </div>
                  )}
                  {shareTestResult.missingScope && (
                    <div className="test-result-info" style={{ marginTop: '0.5rem', padding: '0.75rem', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '4px' }}>
                      <strong>⚠️ Missing Scope:</strong> Re-authorize to grant permissions.
                      <div style={{ marginTop: '0.75rem' }}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleConnectLinkedIn}
                          disabled={generatingOAuthUrl}
                          style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                        >
                          {generatingOAuthUrl ? 'Opening…' : 'Connect LinkedIn (grant permissions)'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="linkedin-posts-section">
            <div className="linkedin-posts-section-header product-schedule-header">
              <h2>Product Schedule</h2>
              <div className="schedule-year-nav">
                <button
                  type="button"
                  className="btn schedule-year-btn"
                  onClick={() => setScheduleYear(y => y - 1)}
                  aria-label="Previous year"
                  disabled={scheduleLoadingYear !== null}
                >
                  ←
                </button>
                <span className="schedule-year-label">{scheduleYear}</span>
                <button
                  type="button"
                  className="btn schedule-year-btn"
                  onClick={() => setScheduleYear(y => y + 1)}
                  aria-label="Next year"
                  disabled={scheduleLoadingYear !== null}
                >
                  →
                </button>
                {scheduleLoadingYear !== null && (
                  <span className="schedule-status schedule-status-loading">Loading…</span>
                )}
                {scheduleSaving && scheduleLoadingYear === null && (
                  <span className="schedule-status schedule-status-saving">Saving…</span>
                )}
              </div>
            </div>
            {scheduleLoadingYear === scheduleYear ? (
              <div className="schedule-loading-placeholder">
                <div className="spinner" />
                <p>Loading schedule for {scheduleYear}…</p>
              </div>
            ) : (
            <div className="product-schedule-table-wrap">
              <table className="product-schedule-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Product</th>
                    <th>Theme</th>
                    <th>Content Ready</th>
                    <th>Best Slot</th>
                    <th>Timezone</th>
                    <th className="schedule-actions-th">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {MONTHS.flatMap((monthName, monthIndex) =>
                    PRODUCT_SCHEDULE_DEFAULTS
                      .filter(product => !isDefaultRowDeleted(scheduleYear, monthIndex, product.id))
                      .map(product => (
                        <tr key={`${monthIndex}-${product.id}`} data-month-index={monthIndex}>
                          <td className="schedule-month-cell schedule-month-with-week">
                            <span className="schedule-month-label">{monthName} (</span>
                            <input
                              type="text"
                              className="schedule-input schedule-input-week"
                              value={getScheduleCell(scheduleYear, product.id, monthIndex, 'week')}
                              onChange={e => updateScheduleCell(scheduleYear, product.id, monthIndex, 'week', e.target.value)}
                              placeholder={String(getISOWeekNumber(scheduleYear, monthIndex))}
                              aria-label={`Week for ${monthName}`}
                            />
                            <span className="schedule-month-label">)</span>
                          </td>
                          <td className="schedule-product-cell">{product.name}</td>
                          <td>
                            <input
                              type="text"
                              className="schedule-input"
                              value={getScheduleCell(scheduleYear, product.id, monthIndex, 'theme')}
                              onChange={e => updateScheduleCell(scheduleYear, product.id, monthIndex, 'theme', e.target.value)}
                            />
                          </td>
<td className="schedule-content-ready-cell">
                          <input
                            type="checkbox"
                            className="schedule-checkbox"
                            checked={!!getScheduleCell(scheduleYear, product.id, monthIndex, 'contentReady')}
                            onChange={e => updateScheduleCell(scheduleYear, product.id, monthIndex, 'contentReady', e.target.checked)}
                            title="Content ready"
                            aria-label="Content ready"
                          />
                        </td>
                        <td className="schedule-slot-cell">
                            {getScheduleCell(scheduleYear, product.id, monthIndex, 'bestSlot')}
                          </td>
                          <td className="schedule-timezone-cell">
                            <select
                              className="schedule-select schedule-select-timezone"
                              value={getScheduleCell(scheduleYear, product.id, monthIndex, 'timezone') || ''}
                              onChange={e => updateScheduleCell(scheduleYear, product.id, monthIndex, 'timezone', e.target.value)}
                            >
                              <option value="">Select timezone</option>
                              {TIMEZONE_OPTIONS.map(tz => (
                                <option key={tz} value={tz}>{tz}</option>
                              ))}
                            </select>
                          </td>
                          <td className="schedule-actions-cell">
                            <button
                              type="button"
                              className="schedule-row-remove-btn"
                              onClick={() => {
                                if (window.confirm('Are you sure you want to delete this row? This cannot be undone.')) {
                                  deleteDefaultRow(scheduleYear, monthIndex, product.id)
                                }
                              }}
                              title="Delete row"
                              aria-label="Delete row"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                  )}
                  {getCustomRows(scheduleYear).map(row => (
                    <tr key={row.id} className="schedule-custom-row">
                      <td className="schedule-month-cell schedule-month-with-week">
                        <select
                          className="schedule-select schedule-select-month"
                          value={row.month === '' || row.month === undefined ? '' : row.month}
                          onChange={e => updateCustomRow(scheduleYear, row.id, 'month', e.target.value === '' ? '' : Number(e.target.value))}
                        >
                          <option value="">—</option>
                          {MONTHS.map((name, i) => (
                            <option key={i} value={i}>{name}</option>
                          ))}
                        </select>
                        <span className="schedule-month-label"> (</span>
                        <input
                          type="text"
                          className="schedule-input schedule-input-week"
                          value={row.week ?? ''}
                          onChange={e => updateCustomRow(scheduleYear, row.id, 'week', e.target.value)}
                          placeholder="Week"
                          aria-label="Week"
                        />
                        <span className="schedule-month-label">)</span>
                      </td>
                      <td className="schedule-product-cell">
                        <input
                          type="text"
                          className="schedule-input"
                          value={row.product ?? ''}
                          onChange={e => updateCustomRow(scheduleYear, row.id, 'product', e.target.value)}
                          placeholder="Product"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="schedule-input"
                          value={row.theme ?? ''}
                          onChange={e => updateCustomRow(scheduleYear, row.id, 'theme', e.target.value)}
                          placeholder="Theme"
                        />
                      </td>
                      <td className="schedule-content-ready-cell">
                        <input
                          type="checkbox"
                          className="schedule-checkbox"
                          checked={!!(row.contentReady === true)}
                          onChange={e => updateCustomRow(scheduleYear, row.id, 'contentReady', e.target.checked)}
                          title="Content ready"
                          aria-label="Content ready"
                        />
                      </td>
                      <td className="schedule-slot-cell">
                        <input
                          type="text"
                          className="schedule-input"
                          value={row.bestSlot ?? ''}
                          onChange={e => updateCustomRow(scheduleYear, row.id, 'bestSlot', e.target.value)}
                          placeholder="e.g. Tue 9am"
                        />
                      </td>
                      <td className="schedule-timezone-cell">
                        <select
                          className="schedule-select schedule-select-timezone"
                          value={row.timezone ?? ''}
                          onChange={e => updateCustomRow(scheduleYear, row.id, 'timezone', e.target.value)}
                        >
                          <option value="">Select timezone</option>
                          {TIMEZONE_OPTIONS.map(tz => (
                            <option key={tz} value={tz}>{tz}</option>
                          ))}
                        </select>
                      </td>
                      <td className="schedule-actions-cell">
                        <button
                          type="button"
                          className="schedule-row-remove-btn"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this row? This cannot be undone.')) {
                              removeCustomRow(scheduleYear, row.id)
                            }
                          }}
                          title="Delete row"
                          aria-label="Delete row"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="schedule-add-row">
                    <td colSpan={7}>
                      <button
                        type="button"
                        className="btn schedule-add-row-btn"
                        onClick={() => addCustomRow(scheduleYear)}
                      >
                        Add row
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            )}
          </div>
        )}

        {activeTab === 'posts' && (
          <>
        <div className="linkedin-posts-section">
          <div className="linkedin-posts-section-header">
            <h2>Create LinkedIn Post</h2>
            <p className="section-description">Create and publish a new post to LinkedIn.</p>
          </div>
          
          <div className="api-test-container">
            <form onSubmit={handleSubmitPost} style={{ width: '100%' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="post-text" style={{ display: 'block', fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--text)' }}>
                  Post Content *
                </label>
                <textarea
                  id="post-text"
                  value={postText}
                  onChange={(e) => setPostText(e.target.value)}
                  placeholder="What do you want to share on LinkedIn?"
                  rows="6"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    minHeight: '120px'
                  }}
                />
                <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '0.25rem' }}>
                  {postText.length} characters
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="post-visibility" style={{ display: 'block', fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--text)' }}>
                  Visibility
                </label>
                <select
                  id="post-visibility"
                  value={postVisibility}
                  onChange={(e) => setPostVisibility(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    fontFamily: 'inherit',
                    background: 'white'
                  }}
                >
                  <option value="PUBLIC">Public - Anyone on LinkedIn</option>
                  <option value="CONNECTIONS">Connections Only</option>
                </select>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '0.25rem' }}>
                  Choose who can see your post
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="post-image" style={{ display: 'block', fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--text)' }}>
                  Attach image (optional)
                </label>
                <input
                  id="post-image"
                  type="file"
                  accept="image/jpeg,image/png,image/gif"
                  onChange={handlePostImageChange}
                  style={{ display: 'block', fontSize: '0.9rem' }}
                />
                <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '0.25rem' }}>
                  JPEG, PNG or GIF. Max {MAX_IMAGE_MB}MB.
                </p>
                {postImagePreview && (
                  <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(0,0,0,0.04)', borderRadius: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <img src={postImagePreview} alt="Preview" style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'contain', borderRadius: '4px' }} />
                      <div style={{ flex: 1, minWidth: '180px' }}>
                        <label htmlFor="post-image-alt" style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.25rem', color: 'var(--text)' }}>
                          Alt text (accessibility)
                        </label>
                        <input
                          id="post-image-alt"
                          type="text"
                          value={postImageAlt}
                          onChange={(e) => setPostImageAlt(e.target.value)}
                          placeholder="Describe the image"
                          maxLength={120}
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }}
                        />
                        <button
                          type="button"
                          className="btn"
                          onClick={clearPostImage}
                          style={{ marginTop: '0.5rem', fontSize: '0.85rem', padding: '0.4rem 0.75rem', background: 'rgba(239, 68, 68, 0.1)', color: 'var(--error, #dc2626)' }}
                        >
                          Remove image
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {!linkedInSettingsLoaded && (
                <p style={{ fontSize: '0.9rem', color: 'var(--text-light)', marginBottom: '1rem' }}>
                  Loading company page settings…
                </p>
              )}
              {linkedInSettingsLoaded && !organizationId.trim() && (
                <div style={{ marginBottom: '1.5rem', padding: '0.75rem', background: 'rgba(251, 191, 36, 0.15)', borderRadius: '6px', border: '1px solid rgba(251, 191, 36, 0.4)' }}>
                  <p style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>Set Organization ID in Admin Settings</p>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>
                    Go to <strong>Admin → LinkedIn Settings</strong> and enter your <strong>Organization ID</strong> to publish to your company page.
                  </p>
                </div>
              )}

              {postResult && (
                <div style={{ 
                  marginBottom: '1.5rem', 
                  padding: '0.75rem', 
                  background: postResult.success ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)', 
                  borderRadius: '6px',
                  border: `1px solid ${postResult.success ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                    {postResult.success ? '✅ Post Published!' : '❌ Failed to Publish'}
                  </p>
                  <p style={{ fontSize: '0.9rem' }}>{postResult.message}</p>
                  {postResult.success && postResult.postUrn && (
                    <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: 'var(--text-light)' }}>
                      Post ID: {postResult.postId || postResult.postUrn}
                    </p>
                  )}
                  {postResult.error && (
                    <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: 'var(--error)' }}>
                      Error: {postResult.error}
                    </p>
                  )}
                  {postResult.missingScope && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <button
                        className="btn btn-primary"
                        onClick={handleReauthorizeForCompany}
                        disabled={generatingOAuthUrl}
                        style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                      >
                        {generatingOAuthUrl ? 'Generating...' : 'Re-authorize for company page (w_organization_social)'}
                      </button>
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '0.5rem' }}>
                        Add &quot;Share on LinkedIn&quot; in Developer Portal → Products first; after approval, click above.
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!linkedInSettingsLoaded || submittingPost || !postText.trim() || !organizationId.trim()}
                  style={{ fontSize: '0.9rem', padding: '0.75rem 1.5rem' }}
                >
                  {submittingPost ? 'Publishing...' : 'Publish to Company Page'}
                </button>
                {postText && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setPostText('')
                      setPostResult(null)
                      clearPostImage()
                    }}
                    style={{ fontSize: '0.9rem', padding: '0.75rem 1.5rem', background: '#f3f4f6', color: '#374151' }}
                  >
                    Clear
                  </button>
                )}
              </div>
              
              <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '6px', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: 'bold' }}>ℹ️ Company Page Requirements:</p>
                <ul style={{ fontSize: '0.85rem', margin: '0', paddingLeft: '1.5rem', color: 'var(--text-light)' }}>
                  <li>Posts are published to your LinkedIn company page only (not your personal profile)</li>
                  <li>You must have admin or editor access to the company page</li>
                  <li>Your access token must have permissions to post on behalf of the company</li>
                </ul>
              </div>
            </form>
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  )
}

export default LinkedInPosts